import { useLazyQuery, useMutation } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { FormEvent, useCallback, useMemo, useState } from "react";

import PrimaryButton from "@components/buttons/PrimaryButton";
import SecondaryButton from "@components/buttons/SecondaryButton";
import BaseErrors from "@components/fields/BaseErrors";
import Form from "@components/forms/Form";
import styles from "@components/forms/PackagePurchaseOptionCheckoutForm.module.css";
import {
  GetClientWithTimeGrantsDocument,
  GetClientWithTimeGrantsQuery,
  GetClientWithTimeGrantsQueryVariables,
  PaymentIntentCreateDocument,
  PaymentIntentCreateMutation,
  PaymentIntentCreateMutationVariables,
  PurchaseCreateDocument,
  PurchaseCreateMutation,
  PurchaseCreateMutationVariables
} from "@typing/Generated";
import { clickCurrentTarget } from "@utils/keyboardUtils";

type Props = {
  clearCurrentPackagePurchaseOption: () => void;
  clientId: string;
  packagePurchaseOptionId: string;
};

const PackagePurchaseOptionCheckoutForm = ({
  clearCurrentPackagePurchaseOption,
  clientId,
  packagePurchaseOptionId
}: Props) => {
  const stripe = useStripe();
  const elements = useElements();

  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [createPaymentIntent] = useMutation<PaymentIntentCreateMutation, PaymentIntentCreateMutationVariables>(
    PaymentIntentCreateDocument
  );

  const [createPurchase] = useMutation<PurchaseCreateMutation, PurchaseCreateMutationVariables>(PurchaseCreateDocument);

  const [getClientWithTimeGrants] = useLazyQuery<GetClientWithTimeGrantsQuery, GetClientWithTimeGrantsQueryVariables>(
    GetClientWithTimeGrantsDocument,
    {
      fetchPolicy: "network-only",
      onCompleted: () => clearCurrentPackagePurchaseOption()
    }
  );

  const fakeHookForm = useMemo(
    () => ({
      formState: {
        isSubmitting: submitting
      }
    }),
    [submitting]
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!stripe || !elements) {
        return;
      }

      setSubmitting(true);

      return createPaymentIntent({ variables: { packagePurchaseOptionId } }).then(response => {
        const intentJourneySecret = response.data?.paymentIntentCreate?.paymentIntent?.clientSecret;

        const cardElement = elements.getElement(CardElement);

        if (intentJourneySecret && cardElement) {
          stripe
            .confirmCardPayment(intentJourneySecret, {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              payment_method: {
                card: cardElement
              }
            })
            .then(response => {
              if (response.error?.message) {
                setBaseErrors([response.error.message]);
                setSubmitting(false);
              } else {
                if (response.paymentIntent?.status === "succeeded") {
                  createPurchase({ variables: { packagePurchaseOptionId, paymentId: response.paymentIntent.id } }).then(
                    () => {
                      setSubmitting(false);
                      // When the update happens, re-pull the time log data from the API. In the onComplete hook
                      // of the query, the current time purchase option will be cleared, taking you back to the account modal
                      getClientWithTimeGrants({ variables: { id: clientId } });
                    }
                  );
                } else {
                  setSubmitting(false);
                }
              }
            })
            .catch(() => {
              setSubmitting(false);
            });
        } else {
          setSubmitting(false);
        }
      });
    },
    [clientId, createPaymentIntent, createPurchase, elements, getClientWithTimeGrants, packagePurchaseOptionId, stripe]
  );

  return (
    <Form className={styles.form} hookForm={fakeHookForm} onSubmit={handleSubmit}>
      <BaseErrors errors={baseErrors} />
      <IonGrid>
        <IonRow>
          <IonCol className={styles.elementWrapper} size-lg="6" size-md="12" size-sm="12">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "18px"
                  }
                }
              }}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
      <PrimaryButton disabled={!stripe || submitting} tabIndex={0} textKey="dictionary.purchase" type="submit" />
      <SecondaryButton
        onClick={clearCurrentPackagePurchaseOption}
        onKeyUp={clickCurrentTarget}
        tabIndex={0}
        textKey="dictionary.cancel"
      />
    </Form>
  );
};

export default PackagePurchaseOptionCheckoutForm;
